import React from 'react'
import classNames from 'classnames'
import { getInputBaseClasses, getInputLabelClasses, getInputHelperTextClasses } from '../utils/inputStyles'

export interface DecimalInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value?: number | null
  onChange?: (value: number | null | undefined) => void
  label?: string
  error?: boolean
  helperText?: string
  decimalPlaces?: number
}

/**
 * Componente de input numérico decimal com formatação automática
 * Permite digitar começando pelos decimais (ex: digitar "123" resulta em "1,23" para 2 decimais)
 */
const DecimalInput = React.forwardRef<HTMLInputElement, DecimalInputProps>(
  ({ 
    value, 
    onChange, 
    label, 
    error, 
    helperText, 
    className = '', 
    disabled, 
    placeholder, 
    id,
    decimalPlaces = 2,
    ...props 
  }, ref) => {
    const inputClasses = getInputBaseClasses(error, disabled, false, className)
    
    const formatter = React.useMemo(
      () => new Intl.NumberFormat('pt-BR', { 
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      }),
      [decimalPlaces]
    )

    const formatNumber = React.useCallback(
      (val?: number | null) => {
        if (val === undefined || val === null) {
          return ''
        }
        return formatter.format(val)
      },
      [formatter]
    )

    const parseNumber = React.useCallback((raw: string, decimals: number): number | null | undefined => {
      const digits = raw.replace(/\D/g, '')
      if (!digits) {
        return null
      }
      const numberValue = Number(digits)
      if (!Number.isFinite(numberValue)) {
        return null
      }
      return numberValue / Math.pow(10, decimals)
    }, [])

    const [displayValue, setDisplayValue] = React.useState<string>(() => formatNumber(value))

    React.useEffect(() => {
      setDisplayValue(formatNumber(value))
    }, [value, formatNumber])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = parseNumber(event.target.value, decimalPlaces)
      onChange?.(next)
      setDisplayValue(next === null || next === undefined ? '' : formatNumber(next))
    }

    return (
      <div className="w-full">
        {label && (
          <label 
            htmlFor={id} 
            className={getInputLabelClasses()}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          className={classNames(inputClasses, 'mt-0')}
          placeholder={placeholder || `0,${'0'.repeat(decimalPlaces)}`}
          {...props}
        />
        {helperText && (
          <p className={getInputHelperTextClasses(error)}>
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

DecimalInput.displayName = 'DecimalInput'

export default DecimalInput

